import axios, { AxiosRequestConfig } from 'axios';
import { loginRequest } from '@auth/authConfig';
import { InteractionRequiredAuthError, SilentRequest, BrowserAuthError, CacheLookupPolicy } from '@azure/msal-browser';
import { getMsalInstance } from '@auth/msalInstance';
import { logError } from '@/AppInsights';

export const axiosInstance = axios.create({
	timeout: 60000,
});

axiosInstance.interceptors.request.use(async function (config) {
	try {
		const token = await getToken();
		if (!token) {
			throw new Error('No token acquired');
		}
		config.headers.Authorization = token ? `Bearer ${token}` : '';
		return config;
	} catch (error: any) {
		logError(new Error(`[Axios]token acquisition failed , ${error}`));
		return Promise.reject(error);
	}
});

axiosInstance.interceptors.response.use(
	(response) => response.data,
	(error) => {
		if (error.response && error.response.status === 401) {
			// Handle 401 error, e.g., redirect to login or refresh token
			console.log('401');
		}
		return Promise.reject(error);
	},
);

let tokenPromise: Promise<string | null> | null = null;
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;

export async function getToken() {
	// Return cached token if still valid (with 10 minute buffer before expiry)
	if (cachedToken && tokenExpiry && Date.now() < tokenExpiry - 10 * 60 * 1000) {
		return cachedToken;
	}

	// If token acquisition is already in progress, return the existing promise
	if (tokenPromise) {
		return tokenPromise;
	}

	// Create a new token acquisition promise
	tokenPromise = (async () => {
		let account = null;
		try {
			const msalInstance = await getMsalInstance();
			account = msalInstance.getActiveAccount();
			const loginRequestConfig = await loginRequest();

			// Try acquireTokenSilent (uses cached tokens/refresh tokens)
			const response = await msalInstance.acquireTokenSilent({
				...loginRequestConfig,
				account,
				cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
			} as SilentRequest);
			if (!response.accessToken) {
				throw new Error(`No access token returned.`);
			}

			// Cache token and expiry
			cachedToken = response.accessToken;
			tokenExpiry = response.expiresOn ? response.expiresOn.getTime() : null;

			return response.accessToken;
		} catch (error: any) {
			logError(new Error(`acquireTokenSilent failed: ${error.message}`));

			// Fall back to interactive auth
			if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
				try {
					const msalInstance = await getMsalInstance();
					const loginRequestConfig: any = await loginRequest();

					const loginHint = account?.username || account?.idTokenClaims?.email || undefined;
					if (loginHint) {
						loginRequestConfig.loginHint = loginHint;
					}

					await msalInstance.acquireTokenRedirect({
						...loginRequestConfig,
					});
				} catch (redirectError) {
				}
				return null;
			}
			return null;
		} finally {
			// Clear the promise cache after acquisition completes (success or failure)
			tokenPromise = null;
		}
	})();

	return tokenPromise;
}

export async function get(url: string, options?: AxiosRequestConfig<unknown> | undefined) {
	return axiosInstance.get(url, options);
}

export async function post(url: string, body?: unknown, options?: AxiosRequestConfig<unknown> | undefined) {
	return axiosInstance.post(url, body, options);
}

export async function put(url: string, body?: unknown, options?: AxiosRequestConfig<unknown> | undefined) {
	return axiosInstance.put(url, body, options);
}

export async function remove(url: string, body?: any) {
	return axiosInstance.delete(url, body);
}

export async function patch(url: string, data?: { refId: string }) {
	return axiosInstance.patch(url, data);
}
